import React, { useEffect, useState } from 'react';

/**
 * Comic book-style chat bubble that appears above leaders
 * @param {Object} props
 * @param {string} props.countryCode - Country code (USA, RUS, CHN, EU, NK)
 * @param {string} props.message - Text to display
 * @param {number} props.duration - How long to show (ms), default 8000 (8 seconds)
 * @param {Function} props.onComplete - Callback when bubble disappears
 */
function ChatBubble({ countryCode, message, duration = 8000, onComplete }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Play bubble pop sound
    try {
      const audio = new Audio('/assets/audio/chat-bubble-pop.wav');
      audio.volume = 0.3; // 30% volume - subtle but noticeable
      audio.play().catch(err => console.debug('Bubble sound not played:', err));
    } catch (err) {
      console.debug('Bubble sound error:', err);
    }
    
    // Fade in
    setTimeout(() => setIsVisible(true), 50);
    
    // Start fade out before complete
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, duration - 500);
    
    // Complete and trigger callback
    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, duration);
    
    return () => {
      clearTimeout(exitTimer);
      clearTimeout(completeTimer);
    };
  }, [duration, onComplete]);

  // Position based on country (approximate leader positions on map)
  const positions = {
    'USA': { left: '20%', top: '35%' },
    'RUS': { left: '65%', top: '25%' },
    'CHN': { left: '75%', top: '40%' },
    'EU': { left: '48%', top: '30%' },
    'NK': { left: '82%', top: '38%' }
  };

  // Country colors for bubble styling
  const colors = {
    'USA': '#3b82f6',
    'RUS': '#ef4444',
    'CHN': '#f59e0b',
    'EU': '#22c55e',
    'NK': '#a855f7'
  };

  const position = positions[countryCode] || { left: '50%', top: '50%' };
  const color = colors[countryCode] || '#ffffff';

  return (
    <div
      className={`fixed z-50 pointer-events-none transition-all duration-500 ${
        isVisible && !isExiting ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
      }`}
      style={{
        left: position.left,
        top: position.top,
        transform: 'translate(-50%, -120%)' // Position above leader
      }}
    >
      {/* Comic book speech bubble */}
      <div
        className="relative bg-white rounded-2xl px-6 py-4 shadow-2xl border-4 max-w-xs"
        style={{
          borderColor: color,
          boxShadow: `0 10px 40px rgba(0,0,0,0.3), 0 0 0 4px ${color}20`
        }}
      >
        {/* Bubble text */}
        <p className="font-bold text-neutral-900 text-base leading-tight text-center">
          {message}
        </p>
        
        {/* Speech bubble tail (pointing down to leader) */}
        <div
          className="absolute left-1/2 -bottom-4 w-0 h-0"
          style={{
            transform: 'translateX(-50%)',
            borderLeft: '20px solid transparent',
            borderRight: '20px solid transparent',
            borderTop: `20px solid ${color}`,
            filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.2))'
          }}
        />
        <div
          className="absolute left-1/2 -bottom-3 w-0 h-0"
          style={{
            transform: 'translateX(-50%)',
            borderLeft: '16px solid transparent',
            borderRight: '16px solid transparent',
            borderTop: '16px solid white',
            zIndex: 10
          }}
        />
      </div>
    </div>
  );
}

export default ChatBubble;
